/**
 * Append-only operational timeline — the `timelineEvents` table introduced in
 * database version 8.
 *
 * This module is the feed's pen and nothing more. Rows are appended, never
 * edited and never deleted; the actions that append them are guarded where they
 * live (`src/db/task-store.ts` and `src/db/assignment-store.ts`), exactly as
 * `recordAuditLog()` is guarded by its callers. `resetTimelineData()` exists
 * only for the same "reset local data" action that wipes the other tables.
 *
 * Ordering is not decided here either: every read goes out through
 * `sortTimelineEvents()` in `src/core/timeline-engine.ts`, so the manager's
 * newest-first ledger and the driver's step-by-step feed are the same rows read
 * two ways.
 */
import { db } from './index';
import { buildTimelineEvent, sortTimelineEvents } from '../core/timeline-engine';
import { TIMELINE_EVENT_LABELS } from '../types/timeline';
import type {
  TimelineEvent,
  TimelineEventDraft,
  TimelineEventType,
  TimelineOrder,
} from '../types/timeline';
import type { User } from '../types/user';

// ── Writes ───────────────────────────────────────────────────────────

/**
 * Appends one step to a task's feed. The caller is the guarded action — this is
 * the pen, not a permission of its own.
 */
export async function logTimelineEvent(draft: TimelineEventDraft): Promise<TimelineEvent> {
  const event = buildTimelineEvent(draft);
  await db.timelineEvents.put(event);
  return event;
}

/** The extra colour a lifecycle step can carry, all of it optional. */
export interface TimelineEventOptions {
  teamId?: string;
  statusTitle?: string;
  description?: string;
  locationText?: string;
  metadata?: Record<string, unknown>;
  timestamp?: string;
}

/**
 * Files one step against the task it belongs to, stamped with the account that
 * reported it. The actor's name and role are copied onto the row so the badge
 * the feed renders survives a rename, a suspension or a roster change.
 */
export async function logTaskTimelineEvent(
  actor: User,
  taskId: string,
  eventType: TimelineEventType,
  options: TimelineEventOptions = {},
): Promise<TimelineEvent> {
  return logTimelineEvent({
    taskId,
    teamId: options.teamId,
    eventType,
    statusTitle: options.statusTitle ?? TIMELINE_EVENT_LABELS[eventType],
    description: options.description,
    actorId: actor.id,
    actorName: actor.fullName,
    actorRole: actor.role,
    locationText: options.locationText,
    metadata: options.metadata,
    timestamp: options.timestamp,
  });
}

// ── Reads ────────────────────────────────────────────────────────────

/**
 * The whole feed of one task, chronologically by default and newest-first on
 * request. The order is the engine's answer, never this module's.
 */
export async function getTaskTimeline(
  taskId: string,
  order: TimelineOrder = 'asc',
): Promise<TimelineEvent[]> {
  if (!taskId) return [];
  const rows = await db.timelineEvents.where('taskId').equals(taskId).toArray();
  return sortTimelineEvents(rows, order);
}

/**
 * One vehicle team's slice of the feed: its own steps plus every step filed
 * against the task as a whole. A crew never reads another vehicle's progress.
 */
export async function getTeamTimeline(
  taskId: string,
  teamId: string,
  order: TimelineOrder = 'asc',
): Promise<TimelineEvent[]> {
  const rows = await getTaskTimeline(taskId, order);
  return rows.filter((event) => !event.teamId || event.teamId === teamId);
}

/** The last step reported on a task, or `null` before anything was. */
export async function getLatestTimelineEvent(taskId: string): Promise<TimelineEvent | null> {
  return (await getTaskTimeline(taskId, 'desc'))[0] ?? null;
}

/** Every step of one kind, across the board — what a report reads. */
export async function listTimelineEvents(filter: {
  taskId?: string;
  teamId?: string;
  eventType?: TimelineEventType;
  actorId?: string;
} = {}): Promise<TimelineEvent[]> {
  const rows = await db.timelineEvents.toArray();
  return sortTimelineEvents(
    rows.filter(
      (row) =>
        (!filter.taskId || row.taskId === filter.taskId) &&
        (!filter.teamId || row.teamId === filter.teamId) &&
        (!filter.eventType || row.eventType === filter.eventType) &&
        (!filter.actorId || row.actorId === filter.actorId),
    ),
    'desc',
  );
}

// ── Maintenance ──────────────────────────────────────────────────────

/**
 * Removes the feed of one task. The ledger is append-only for as long as the
 * task exists — this is only ever reached from `purgeTask()`, once the task
 * itself is being removed from the recycle bin for good.
 */
export async function deleteTaskTimeline(taskId: string): Promise<number> {
  if (!taskId) return 0;
  return db.timelineEvents.where('taskId').equals(taskId).delete();
}

/** Wipes the feed — part of the "reset local data" action only. */
export async function resetTimelineData(): Promise<void> {
  await db.timelineEvents.clear();
}
